import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { SubsProvider } from "./contexts/SubsContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { TermsModalProvider } from "./contexts/TermsModalContext";
import Layout from "./components/Layout";
import AuthModal from "./components/AuthModal";
import Home from "./pages/Home";
import YourPrayers from "./pages/YourPrayers";
import AddPrayer from "./pages/AddPrayer";
import WatchPrayer from "./pages/WatchPrayer";
import ManagePrayers from "./pages/ManagePrayers";
import ComingSoon from "./pages/ComingSoon";
import Circles from "./pages/Circles";
import Settings from "./pages/Settings";
import LitCandles from "./pages/LitCandles";
import About from "./pages/About";
import Terms from "./pages/Terms";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminReports from "./pages/AdminReports";
import AdminUsers from "./pages/AdminUsers";
import AdminActivity from "./pages/AdminActivity";
import AdminFeedback from "./pages/AdminFeedback";
import AdminMisuseReports from "./pages/AdminMisuseReports";
import AdminPrayers from "./pages/AdminPrayers";
import AdminCloudinaryCleanup from "./pages/AdminCloudinaryCleanup";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminLinkPreview from "./pages/AdminLinkPreview";
import AdminObservability from "./pages/AdminObservability";
import Feedback from "./pages/Feedback";
import JoinCircle from "./pages/JoinCircle";
import Notifications from "./pages/Notifications";
import HashtagResults from "./pages/HashtagResults";
import PrayerHistory from "./pages/PrayerHistory";
import CirclePage from "./pages/CirclePage";

// Force AddPrayer to unmount + remount when the URL transitions between
// /add-prayer and /edit-prayer/:id (or between two /edit-prayer/:id
// slugs). React Router 6 reuses the same component instance when both
// routes render the same JSX element, which caused prior title /
// description / cover-image state to leak across saves. Keying by the
// editing id (or the literal 'create' sentinel) triggers a clean
// remount so every visit lands on the empty initial-state defaults.
const AddPrayerRoute = () => {
  const { id } = useParams();
  return <AddPrayer key={id || 'create'} />;
};

function AppRouter() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/add-prayer" element={<AddPrayerRoute />} />
        <Route path="/edit-prayer/:id" element={<AddPrayerRoute />} />
        <Route path="/prayer/:id" element={<WatchPrayer />} />
        <Route path="/manage-prayers" element={<ManagePrayers />} />
        <Route path="/feed/clips" element={<YourPrayers />} />
        <Route path="/circles" element={<Circles />} />
        <Route path="/circle/:userId" element={<CirclePage />} />
        <Route path="/circles/join/:authorUserId" element={<JoinCircle />} />
        <Route path="/feed/subscriptions" element={<Circles />} />

        {/* Legacy video pages — hidden UI kept, replaced with Coming Soon placeholders */}
        <Route path="/watch" element={<ComingSoon title="Video player" />} />
        <Route path="/results" element={<ComingSoon title="Search" />} />
        <Route path="/shorts" element={<ComingSoon title="Shorts" />} />
        <Route path="/feed/playlists" element={<ComingSoon title="Prayer Lists" />} />
        <Route path="/feed/trending" element={<ComingSoon title="Trending" />} />
        <Route path="/feed/liked" element={<LitCandles />} />
        <Route path="/feed/watch-later" element={<ComingSoon title="Bookmarked" />} />
        <Route path="/channel/:id" element={<ComingSoon title="Channel" />} />
        <Route path="/shop" element={<ComingSoon title="Shop" />} />
        <Route path="/profile" element={<Settings view="profile" />} />
        <Route path="/settings" element={<Settings view="settings" />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/history" element={<PrayerHistory />} />
        <Route path="/tag/:tag" element={<HashtagResults />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin/reports" element={<AdminReports />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/activity" element={<AdminActivity />} />
        <Route path="/admin/feedback" element={<AdminFeedback />} />
        <Route path="/admin/misuse-reports" element={<AdminMisuseReports />} />
        <Route path="/admin/prayers" element={<AdminPrayers />} />
        <Route path="/admin/cloudinary-cleanup" element={<AdminCloudinaryCleanup />} />
        <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
        <Route path="/admin/link-preview" element={<AdminLinkPreview />} />
        <Route path="/admin/observability" element={<AdminObservability />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/help" element={<ComingSoon title="Gifts" />} />

        <Route path="*" element={<Home />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <SubsProvider>
            <NotificationsProvider>
              <TermsModalProvider>
                <AppRouter />
                <AuthModal />
              </TermsModalProvider>
            </NotificationsProvider>
          </SubsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
